import ExpenseInfo from '../expense-info/expense-info.jsx';
import GuestCountInput from '../guest-count-input/guest-count-input.jsx';
import LocationInput from '../location-input/location-input.jsx';
import MileageInput from '../mileage-input/mileage-input.jsx';
import MatterSplitInput from '../matter-split-input/matter-split-input.jsx';
import AsyncSelect from 'react-select/async';
import Select from 'react-select';
import { inject } from 'mobx-react';
import { Fragment, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import useLanguageChange from '../../../utils/hooks/useLanguageChange';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { cachedFetch } from '../../../utils/cached-api-wrapper';
import { fetch } from '../../../utils/api-wrapper';
import {
  appliedMatterRule,
  findExactMatterSearchHit,
  matterAllowedByRestriction,
  matterSearchUrl,
  toMatterSelectOption,
} from '../../../utils/matter-search';
import Utils, { getSelectStyles } from '../../../utils/utils';
import { getExpenseFormLabel } from '../../../utils/expense-form-labels';
import {
  imputationDocumentId,
  resolveImputationTaxCodeFromRows,
  resolveImputationCodingFromRows,
} from '../utils/contract-expense-json';
import {
  buildImputationsListUrl,
  deletedImputationOptionLabel,
  formatImputationSelectOptionLabel,
  imputationDisplayLabel,
  isSoftDeletedImputation,
  resolveStoredImputationMissingFromList,
  sortImputationRowsByDisplayLabel,
} from '../../../utils/expenses-imputations-config-helpers';
import { isActivityVisible, isLocationVisible, isMileageVisible, isNumberOfGuestsRequired, resolveVerifyCategoryFromImputationRow, shouldShowNumberOfGuests } from '../utils/conditional-fields';
import './expense-component.scss';
import ReviewFacilityField from '../review-facility-field';
import SegmentRuleField from '../../../components/segment-rule-field';
import { GL_FACILITY, hasSegmentRules } from '../../../utils/gl-segment-rules';

function buildImputationTaxCodeInfoPatch(data, level1Row, leafRow) {
  if (!Utils.hasExpenseField(data, 'field_tax_code')) {
    return {};
  }
  const taxCode = resolveImputationTaxCodeFromRows({ level1Row, leafRow });
  return taxCode ? { info: { taxCode } } : {};
}

/**
 * XPEN-2939: apply imputation Cost Type / GL Natural / office on category change.
 * Always patches form state (even when field_cost_type is hidden) so save can refresh coding.
 * Uses '' (not null) so JSON.stringify keeps CostType and clears stale Travel codes.
 */
function buildImputationCodingPatch(level1Row, leafRow) {
  const coding = resolveImputationCodingFromRows({ level1Row, leafRow });
  return {
    costType: coding.cost_type || '',
    imputationCoding: coding,
  };
}

function isExpenseBillable(expense) {
  return expense?.isBillable !== false;
}

function isBlank(value) {
  return value == null || String(value).trim() === '';
}

const RequiredMark = () => <span className='text-danger'>*</span>;

const FormField = ({ label, children, required = false, error, touched, errorMessage, className = '' }) => (
  <div className={`d-flex pb-2 ${className}`}>
    <span className='report-label col-2'>
      {label} {required && <RequiredMark />}
    </span>
    <div className='col-8 mb-2'>
      {children}
      {error && touched && <div className='text-danger mt-1 pl-1'>{errorMessage}</div>}
    </div>
  </div>
);

const ExpenseComponent = ({
  data,
  expense,
  expenseIndex,
  updateInfo,
  removeExpense,
  onValidationChange,
  onImputationLeafStageChange,
  onImputationRowsChange,
  deletable,
  imputationReviewForUserId,
  showHeading = true,
  cardLayout = false,
  facilityPreview,
  onExpenseDateChange,
  hideNarrative = false,
  compactOptionalFields = false,
  menuPortalTarget = document.body,
  // XPEN-3551: the invoice editor's add-expense modal creates receipts only, one
  // line each, so it turns both off. The review screen keeps the defaults.
  allowMileage = true,
  allowMatterSplit = true,
  // XPEN-3551: optional. Called when the category list cannot be loaded (e.g. 403
  // when the caller may not code as the report owner), so the host can say so
  // instead of showing an empty list.
  onImputationCatalogError,
  // XPEN-3653: optional. A client type someone really chose outside the form
  // ('client' | 'internal'), e.g. the invoice editor table whose "+" opened the
  // add-expense window. Used only when the Expense Type field is hidden.
  chosenExpenseType,
}) => {
  const isElite = Utils.isExportFormat(data, 'elite');
  // XPEN-3653: with the Expense Type field hidden, nobody can change the type on
  // screen, so the category decides it and must never lock the form.
  const expenseTypeIsShown = Utils.hasExpenseField(data, 'field_expense_type', false);
  // XPEN-3653-006: when the form sets the type, a Client type is billable (decided
  // 2026-10-12). With the Billable field hidden, isBillableSetByType tells the save to
  // keep that Billable; otherwise the stored one is kept (it may have been changed in
  // the expense editor). A Non-client save is always non-billable.
  const billableIsShown = Utils.hasExpenseField(data, 'field_billable', false);
  const billableSetByType = billableIsShown ? {} : { isBillableSetByType: true };
  // The category list follows a type the person chose (the field, or the
  // window's table). A type a category set must not hide the other categories.
  const rootFilterType = expenseTypeIsShown ? expense.expenseType : (chosenExpenseType ?? '');
  /**
   * XPEN-3653: type of an expense coded to these rows when the Expense Type
   * field is hidden. Non-client → 'internal'; Client or "No default" → 'client',
   * unless the window's table chose otherwise. Picking a category passes no
   * leaf (null); the opening effect passes the stored subcategory, whose
   * client_filter then wins over the category default.
   */
  const hiddenExpenseTypeFor = (level1Row, leafRow) =>
    leafRow?.client_filter || level1Row?.default_expense_type || chosenExpenseType || 'client';
  const [imputationRoots, setImputationRoots] = useState([]);
  const [imputationChildren, setImputationChildren] = useState([]);
  const [staleImputationRoot, setStaleImputationRoot] = useState(null);
  const [staleImputationLeaf, setStaleImputationLeaf] = useState(null);
  const [imputationChildrenLoading, setImputationChildrenLoading] = useState(false);
  const expenseRef = useRef(expense);
  expenseRef.current = expense;
  const imputationRootsFetchGenRef = useRef(0);
  const imputationChildrenFetchGenRef = useRef(0);
  const lastRootResolveKeyRef = useRef(null);
  const lastLeafResolveKeyRef = useRef(null);
  const matterSplitsConversionRef = useRef(0);
  // XPEN-2779: tracks which level1Id+leafId combo we last evaluated so we don't
  // re-apply the init default if the user manually clears expenseType afterward.
  const defaultExpenseTypeAppliedForRef = useRef(null);
  // XPEN-3759: the one category id sent to the Matter search (XPEN-3757), whose
  // rule the server then applies if it has one: the subcategory when one is picked,
  // else the category. None when the Category field is hidden: the person cannot
  // see or pick a category then (the save still sends the stored ids,
  // resolveImputationIdsForPayload in contract-expense-json.js).
  const matterImputationId = Utils.hasExpenseField(data, 'field_imputation_level1', false)
    ? (expense.imputationLeafId || expense.imputationLevel1Id || null)
    : null;
  const matterImputationIdRef = useRef(matterImputationId);
  matterImputationIdRef.current = matterImputationId;
  // XPEN-3759: changes with that category, so the next search does not reuse results
  // react-select cached for the old one, and (as a key) the dropdown remounts.
  const matterOptionsCacheKey = `matters:${matterImputationId ?? ''}`;

  /**
   * XPEN-3759: adds to `patch` the clearing of every Matter `rule` does not allow,
   * the single Matter and each split row (its percentage kept). Only a person's
   * category or subcategory pick calls it, never the form opening.
   */
  const clearMattersNotAllowed = (rule, patch) => {
    const current = expenseRef.current;
    if (current.matter && !matterAllowedByRestriction(rule, current.matter)) {
      patch.matter = '';
    }
    if (Array.isArray(current.matterSplits)) {
      let changed = false;
      const splits = current.matterSplits.map((split) => {
        if (split?.matter && !matterAllowedByRestriction(rule, split.matter)) {
          changed = true;
          return { ...split, matter: '' };
        }
        return split;
      });
      if (changed) patch.matterSplits = splits;
    }
  };

  const getMatterInfo = (number, callback) => {
    if (!number) return callback(null);
    // `search` matches number OR altNumber (XPEN-3080), so a line stored as the
    // alternate number still resolves. It is a substring match, so keep only an
    // exact number/altNumber hit — never resolve a look-alike matter.
    // Window is the search endpoint default (15), same as expense.js. Do not
    // use `/matters/search/<text>/<limit>`: the API passes `limit` through as a
    // string and the search then returns [].
    cachedFetch(
      matterSearchUrl(number),
      'GET',
      {},
      (response) => {
        const hits = Array.isArray(response?.data) ? response.data : [];
        const exact = findExactMatterSearchHit(hits, number);
        callback(exact ?? null);
      },
      () => callback(null)
    );
  };

  const getCostTypeInfo = (cost_type, callback) => {
    if (!cost_type) return callback(null);
    const filters = [{ id: 'id_client', value: data.id_client }, { id: 'cost_type', value: cost_type }];
    const pageSize = 1;
    const searchData = `?filters=${encodeURIComponent(JSON.stringify(filters ?? []))}&page_size=${pageSize}`
    cachedFetch(`/cost_types/${searchData}`, "GET", {}, (response) => {
        if (callback && response?.data?.cost_types && response.data.cost_types?.length > 0) {
            return callback(response.data.cost_types[0]);
        }
        callback(null);
    });
  };

  const loadMatters = (search_query = '', callback = () => {}) => {
    const query = String(search_query ?? '').trim();
    // Offer the first fifty on open; typed searches retain their existing limit.
    const requestedFor = matterImputationId;
    cachedFetch(
      matterSearchUrl(query, { imputationId: requestedFor, limit: query ? undefined : 50 }),
      'GET',
      {},
      (response) => {
        // XPEN-3759: an answer for a category no longer selected is dropped, so it
        // never reaches the remounted dropdown (after A -> B -> A, an A answer still
        // passes: it is right for A, and its old dropdown is already gone).
        if (matterImputationIdRef.current !== requestedFor) return;
        const hits = Array.isArray(response?.data) ? response.data : [];
        callback(hits.map((hit) => toMatterSelectOption(data, hit)).filter(Boolean));
      },
      () => {
        if (matterImputationIdRef.current !== requestedFor) return;
        callback([]);
      },
      false,
      null,
      { cacheType: 'autocomplete' }
    );
  };

  const loadCostTypes = (search_query = '', callback = () => {}) => {
    const filters = [{ id: 'id_client', value: data.id_client }, { id: 'is_expense_form', value: true }];
    if (search_query.length > 0) {
      filters.push({ id: 'search', value: search_query });
    }
    const sorting = [{ id: 'name', desc: false }];
    const pageSize = 20;
    const searchData = `?filters=${encodeURIComponent(JSON.stringify(filters ?? []))}&sorting=${encodeURIComponent(
      JSON.stringify(sorting ?? [])
    )}&page_size=${pageSize}`;
    cachedFetch(
      `/cost_types/${searchData}`,
      'GET',
      {},
      (response) => {
        const options =
          response?.data?.cost_types?.map((m) => (({
          label: m.cost_type ? `${m.cost_type}${m.description ? ' - ' + m.description : ''}` : '',
          value: m,
        }))) ?? [];
      callback(options);
    }, null, false, null, { cacheType: 'autocomplete' });
  };

  const loadActivities = useCallback((search_query = "", callback = () => {}) => {
    const matter_id = expense.matter?.value?.number ?? expense.matter?.number ?? "";
    const val = search_query.trim().replace(/\//g, '');
    const searchData = `${encodeURIComponent(val)}?matter_id=${matter_id}`;
    fetch(`/activities/search/${searchData}`, "GET", {}, (response) => {
      const activities = response.data;
      callback(activities.map(activity => ({
        value: activity,
        code: activity.code,
        description: activity.description,
        activity_id: activity.activity_id,
        activity_list: activity.activity_list,
        label: `${activity.code} - ${activity.description}`,
      })));
    });
  }, [expense.matter]);

  const [errors, setErrors] = useState({
    matter: Utils.isExpenseFieldRequired(data, 'field_matter') && !expense.matter && expense.expenseType !== 'internal',
    date: Utils.isExpenseFieldRequired(data, 'field_expense_date') && !expense.date,
    costType: Utils.isExpenseFieldRequired(data, 'field_cost_type') && !expense.costType,
    info: !expense.info,
    location: false,
    activity: false,
    imputationLevel1: false,
    imputationLeaf: false,
  });

  const [touched, setTouched] = useState({
    matter: false,
    date: false,
    costType: false,
    info: false,
    location: false,
    activity: false,
    imputationLevel1: false,
    imputationLeaf: false,
  });

  useLanguageChange(data.t);

  useEffect(() => {
    if (!Utils.hasExpenseField(data, 'field_imputation_level1', false)) return;
    if (imputationReviewForUserId === undefined) {
      imputationRootsFetchGenRef.current += 1;
      lastRootResolveKeyRef.current = null;
      lastLeafResolveKeyRef.current = null;
      setImputationRoots([]);
      return;
    }
    const fetchGen = ++imputationRootsFetchGenRef.current;
    // XPEN-2819: send client_filter on the roots fetch (mirrors the children
    // fetch) so the API can hide dead-end categories — a parent whose only
    // subcategories are filtered out for the chosen expense type. When the
    // expense type is empty/cleared, no filter is sent and all roots show.
    // XPEN-3653: with the Expense Type field hidden, only a chosen type
    // (chosenExpenseType) filters the roots — never one a category set.
    const rootFilters = [{ id: 'roots_only', value: true }];
    if (rootFilterType === 'client' || rootFilterType === 'internal') {
      rootFilters.push({ id: 'client_filter', value: rootFilterType });
    }
    fetch(
      buildImputationsListUrl(rootFilters, imputationReviewForUserId, data.id_client),
      'GET',
      {},
      (response) => {
        if (fetchGen !== imputationRootsFetchGenRef.current) return;
        const roots = response?.data?.imputations ?? [];
        const hasClientRoots = roots.some((r) => r.origin === 'client');
        const filteredRoots = hasClientRoots ? roots.filter((r) => r.origin === 'client') : roots;
        setImputationRoots(filteredRoots);
        const l1 = expenseRef.current.imputationLevel1Id;
        const lookedUpId = l1 != null ? String(l1) : '';
        lastRootResolveKeyRef.current = lookedUpId;
        resolveStoredImputationMissingFromList(
          {
            storedId: l1,
            liveRows: filteredRoots,
            reviewForUserId: imputationReviewForUserId,
            fetchImpl: fetch,
          },
          (result) => {
            if (fetchGen !== imputationRootsFetchGenRef.current) return;
            if (String(expenseRef.current.imputationLevel1Id ?? '') !== lookedUpId) return;
            if (result.action === 'deleted') {
              setStaleImputationRoot(result.row);
              return;
            }
            if (result.action === 'error') {
              return;
            }
            setStaleImputationRoot(null);
            if (result.action === 'clear' && lookedUpId) {
              updateInfo({ imputationLevel1Id: null, imputationLeafId: null });
            }
          }
        );
      },
      (error) => {
        if (fetchGen !== imputationRootsFetchGenRef.current) return;
        onImputationCatalogError?.(error);
      }
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch roots when review_for_user / account owner changes (XPEN-2455); the filter type added so dead-end categories are re-filtered when it changes (XPEN-2819, XPEN-3653)
  }, [imputationReviewForUserId, rootFilterType]);

  useEffect(() => {
    if (!Utils.hasExpenseField(data, 'field_imputation_level1', false)) {
      imputationChildrenFetchGenRef.current += 1;
      setImputationChildren([]);
      setImputationChildrenLoading(false);
      onImputationLeafStageChange?.(expenseIndex, 'optional');
      return;
    }
    if (imputationReviewForUserId === undefined) {
      imputationChildrenFetchGenRef.current += 1;
      lastLeafResolveKeyRef.current = null;
      setImputationChildren([]);
      setImputationChildrenLoading(false);
      onImputationLeafStageChange?.(
        expenseIndex,
        expense.imputationLevel1Id ? 'loading' : 'optional'
      );
      return;
    }
    if (!expense.imputationLevel1Id) {
      imputationChildrenFetchGenRef.current += 1;
      lastLeafResolveKeyRef.current = null;
      setImputationChildren([]);
      setImputationChildrenLoading(false);
      setStaleImputationLeaf(null);
      onImputationLeafStageChange?.(expenseIndex, 'optional');
      return;
    }
    const fetchParentId = String(expense.imputationLevel1Id);
    const fetchGen = ++imputationChildrenFetchGenRef.current;
    lastLeafResolveKeyRef.current = null;
    const childFilters = [{ id: 'parent_id', value: fetchParentId }];
    if (expense.expenseType === 'client' || expense.expenseType === 'internal') {
      childFilters.push({ id: 'client_filter', value: expense.expenseType });
    }
    setImputationChildren([]);
    setImputationChildrenLoading(true);
    onImputationLeafStageChange?.(expenseIndex, 'loading');
    fetch(
      buildImputationsListUrl(childFilters, imputationReviewForUserId, data.id_client),
      'GET',
      {},
      (response) => {
        if (fetchGen !== imputationChildrenFetchGenRef.current) return;
        if (String(expenseRef.current.imputationLevel1Id) !== fetchParentId) {
          return;
        }
        const rows = response?.data?.imputations ?? [];
        const hasClientChildren = rows.some((r) => r.origin === 'client');
        const filteredRows = hasClientChildren ? rows.filter((r) => r.origin === 'client') : rows;
        setImputationChildren(filteredRows);
        setImputationChildrenLoading(false);
        const leafId = expenseRef.current.imputationLeafId;
        const lookedUpId = leafId != null ? String(leafId) : '';
        lastLeafResolveKeyRef.current = lookedUpId;
        resolveStoredImputationMissingFromList(
          {
            storedId: leafId,
            liveRows: filteredRows,
            reviewForUserId: imputationReviewForUserId,
            fetchImpl: fetch,
          },
          (result) => {
            if (fetchGen !== imputationChildrenFetchGenRef.current) return;
            if (String(expenseRef.current.imputationLevel1Id) !== fetchParentId) return;
            if (String(expenseRef.current.imputationLeafId ?? '') !== lookedUpId) return;
            if (result.action === 'deleted') {
              setStaleImputationLeaf(result.row);
              return;
            }
            if (result.action === 'error') {
              return;
            }
            setStaleImputationLeaf(null);
            if (result.action === 'clear' && lookedUpId) {
              updateInfo({ imputationLeafId: null });
            }
          }
        );
        onImputationLeafStageChange?.(
          expenseIndex,
          filteredRows.length > 0 ? 'required' : 'optional'
        );
      },
      () => {
        if (fetchGen !== imputationChildrenFetchGenRef.current) return;
        if (String(expenseRef.current.imputationLevel1Id) !== fetchParentId) {
          return;
        }
        setImputationChildren([]);
        setImputationChildrenLoading(false);
        onImputationLeafStageChange?.(expenseIndex, 'optional');
      }
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch callbacks are stable; listing them would re-fetch on every render (XPEN-2744: expense.expenseType added for default_expense_type filter)
  }, [expense.imputationLevel1Id, imputationReviewForUserId, expense.expenseType]);

  useEffect(() => {
    if (lastRootResolveKeyRef.current == null) return;
    const l1 = expense.imputationLevel1Id;
    const lookedUpId = l1 != null ? String(l1) : '';
    if (lastRootResolveKeyRef.current === lookedUpId) return;
    lastRootResolveKeyRef.current = lookedUpId;
    resolveStoredImputationMissingFromList(
      {
        storedId: l1,
        liveRows: imputationRoots,
        reviewForUserId: imputationReviewForUserId,
        fetchImpl: fetch,
      },
      (result) => {
        if (lastRootResolveKeyRef.current !== lookedUpId) return;
        if (String(expenseRef.current.imputationLevel1Id ?? '') !== lookedUpId) return;
        if (result.action === 'deleted') {
          setStaleImputationRoot(result.row);
          return;
        }
        if (result.action === 'error') {
          return;
        }
        setStaleImputationRoot(null);
        if (result.action === 'clear' && lookedUpId) {
          updateInfo({ imputationLevel1Id: null, imputationLeafId: null });
        }
      }
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps -- updateInfo is a stable parent callback
  }, [imputationRoots, expense.imputationLevel1Id, imputationReviewForUserId]);

  useEffect(() => {
    if (lastLeafResolveKeyRef.current == null) return;
    const leafId = expense.imputationLeafId;
    const lookedUpId = leafId != null ? String(leafId) : '';
    if (lastLeafResolveKeyRef.current === lookedUpId) return;
    lastLeafResolveKeyRef.current = lookedUpId;
    resolveStoredImputationMissingFromList(
      {
        storedId: leafId,
        liveRows: imputationChildren,
        reviewForUserId: imputationReviewForUserId,
        fetchImpl: fetch,
      },
      (result) => {
        if (lastLeafResolveKeyRef.current !== lookedUpId) return;
        if (String(expenseRef.current.imputationLeafId ?? '') !== lookedUpId) return;
        if (result.action === 'deleted') {
          setStaleImputationLeaf(result.row);
          return;
        }
        if (result.action === 'error') {
          return;
        }
        setStaleImputationLeaf(null);
        if (result.action === 'clear' && lookedUpId) {
          updateInfo({ imputationLeafId: null });
        }
      }
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps -- updateInfo is a stable parent callback
  }, [imputationChildren, expense.imputationLeafId, imputationReviewForUserId]);

  // Convert costType from string to full cost type object when needed
  useEffect(() => {
    if (expense.costType && typeof expense.costType === 'string') {
      const costTypeString = expense.costType;
      getCostTypeInfo(costTypeString, (costTypeInfo) => {
        if (costTypeInfo) {
          updateInfo({
            costType: { value: costTypeInfo, label: costTypeInfo.cost_type ? `${costTypeInfo.cost_type}${costTypeInfo.description ? ' - ' + costTypeInfo.description : ''}` : '' },
          });
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expense.costType]);

  // Convert matter from string (number) to full matter object when needed
  useEffect(() => {
    if (expense.matter && typeof expense.matter === 'string') {
      const matterString = expense.matter;
      getMatterInfo(matterString, (matterInfo) => {
        // XPEN-3759: a category pick may have cleared this Matter while the lookup ran.
        if (expenseRef.current.matter !== matterString) return;
        if (matterInfo) {
          updateInfo({
            matter: toMatterSelectOption(data, matterInfo),
          });
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expense.matter]);

  // Convert string MatterIds in matterSplits to SelectOptions on reload.
  // XPEN-3737: every change to matterSplits starts a new generation, before any early
  // return, so a lookup still in flight never writes an older split back over a newer
  // one (Single matter sets null; a person may change a row). A number the search cannot
  // find stays as text (the save sends it as is, XPEN-3735), and nothing is written back
  // when no row was found, so the effect does not re-trigger itself.
  useEffect(() => {
    const gen = ++matterSplitsConversionRef.current;
    if (!Array.isArray(expense.matterSplits)) return;
    const needsConversion = expense.matterSplits.some(
      (s) => typeof s.matter === 'string' && s.matter !== ''
    );
    if (!needsConversion) return;
    const resolved = expense.matterSplits.map(
      (s) =>
        new Promise((resolve) => {
          if (typeof s.matter !== 'string' || s.matter === '') {
            resolve(s);
          } else {
            getMatterInfo(s.matter, (matterInfo) => {
              if (matterInfo) {
                resolve({
                  ...s,
                  matter: toMatterSelectOption(data, matterInfo),
                });
              } else {
                resolve(s);
              }
            });
          }
        })
    );
    Promise.all(resolved).then((updatedSplits) => {
      if (gen !== matterSplitsConversionRef.current) return;
      if (updatedSplits.every((split, index) => split === expense.matterSplits[index])) return;
      updateInfo({ matterSplits: updatedSplits });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getMatterInfo and updateInfo are stable references; matterSplits identity change is the correct trigger for string-to-SelectOption conversion
  }, [expense.matterSplits]);

  // XPEN-2779: on form open, when the engine pre-filled imputationLevel1Id (and optionally
  // imputationLeafId), apply the default expenseType so the field is not blank.
  // Priority with the field visible: leaf.client_filter (if set) → root.default_expense_type.
  // The ref prevents re-application if the user later manually clears expenseType.
  // XPEN-3653: with the Expense Type field hidden this runs too, through
  // hiddenExpenseTypeFor: leaf.client_filter → root.default_expense_type →
  // chosenExpenseType → 'client' ("No default" = client). A type the expense
  // already has (e.g. from the Ingestor or an earlier save) is kept, and so is its
  // Billable. XPEN-3653-006: a Client type set here is billable.
  useEffect(() => {
    if (!expense.imputationLevel1Id) return;
    if (imputationRoots.length === 0) return;
    if (expense.expenseType) return;

    const key = `${expense.imputationLevel1Id}-${expense.imputationLeafId ?? ''}`;
    if (defaultExpenseTypeAppliedForRef.current === key) return;

    if (expense.imputationLeafId && (imputationChildrenLoading || imputationChildren.length === 0)) return;

    const root = imputationRoots.find((r) => imputationDocumentId(r) === String(expense.imputationLevel1Id));
    if (!root) return;

    const leaf = expense.imputationLeafId
      ? imputationChildren.find((r) => imputationDocumentId(r) === String(expense.imputationLeafId))
      : null;

    defaultExpenseTypeAppliedForRef.current = key;

    if (!expenseTypeIsShown) {
      // Internal is non-billable and remembers the Billable it replaced (see the
      // Category onChange); Client is billable (XPEN-3653-006).
      const hiddenType = hiddenExpenseTypeFor(root, leaf);
      updateInfo(hiddenType === 'internal'
        ? { expenseType: 'internal', isBillableBeforeInternal: expenseRef.current.isBillable, isBillable: false }
        : { expenseType: hiddenType, isBillable: true, ...billableSetByType });
      return;
    }

    const resolvedExpenseType = leaf?.client_filter || root.default_expense_type || null;
    if (resolvedExpenseType) {
      updateInfo({
        expenseType: resolvedExpenseType,
        isBillable: resolvedExpenseType === 'client',
        ...(resolvedExpenseType === 'client' ? billableSetByType : {}),
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- expense.expenseType read as bail-out only (stale read is safe; the ref is the authoritative re-application guard); updateInfo and data are stable prop references; expenseTypeIsShown and billableSetByType come from client config and chosenExpenseType (read through hiddenExpenseTypeFor) is set once by the add-expense window, its only caller; expenseRef is a ref
  }, [imputationRoots, imputationChildren, imputationChildrenLoading, expense.imputationLevel1Id, expense.imputationLeafId]);

  // XPEN-2939: keep resolved imputation rows in sync for save payload (parity with expense.js).
  useEffect(() => {
    if (!Utils.hasExpenseField(data, 'field_imputation_level1', false)) {
      onImputationRowsChange?.({ level1Row: null, leafRow: null });
      return;
    }
    if (!expense.imputationLevel1Id || imputationRoots.length === 0) {
      onImputationRowsChange?.({ level1Row: null, leafRow: null });
      return;
    }
    if (expense.imputationLeafId && (imputationChildrenLoading || imputationChildren.length === 0)) {
      return;
    }

    const level1Row = imputationRoots.find(
      (r) => imputationDocumentId(r) === String(expense.imputationLevel1Id)
    ) ?? null;
    const leafRow = expense.imputationLeafId
      ? (imputationChildren.find(
          (r) => imputationDocumentId(r) === String(expense.imputationLeafId)
        ) ?? null)
      : null;
    onImputationRowsChange?.({ level1Row, leafRow });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- onImputationRowsChange is a stable ref callback from parent
  }, [
    data,
    expense.imputationLevel1Id,
    expense.imputationLeafId,
    imputationRoots,
    imputationChildren,
    imputationChildrenLoading,
    onImputationRowsChange,
  ]);

  const rootSelectValue = useMemo(() => {
    if (!expense.imputationLevel1Id) return null;
    const live = imputationRoots.find(
      (r) => imputationDocumentId(r) === String(expense.imputationLevel1Id)
    );
    if (live) return { value: live, label: imputationDisplayLabel(live, data) };
    if (
      staleImputationRoot &&
      imputationDocumentId(staleImputationRoot) === String(expense.imputationLevel1Id)
    ) {
      return {
        value: staleImputationRoot,
        label: deletedImputationOptionLabel(
          staleImputationRoot,
          data.t.read('review_expense.imputation_deleted'),
          data
        ),
        isDeleted: true,
      };
    }
    return null;
  }, [expense.imputationLevel1Id, imputationRoots, staleImputationRoot, data, data.t.lang, data.xpensCategories]);

  const leafSelectValue = useMemo(() => {
    if (!expense.imputationLeafId) return null;
    const live = imputationChildren.find(
      (r) => imputationDocumentId(r) === String(expense.imputationLeafId)
    );
    if (live) return { value: live, label: imputationDisplayLabel(live, data) };
    if (
      staleImputationLeaf &&
      imputationDocumentId(staleImputationLeaf) === String(expense.imputationLeafId)
    ) {
      return {
        value: staleImputationLeaf,
        label: deletedImputationOptionLabel(
          staleImputationLeaf,
          data.t.read('review_expense.imputation_deleted'),
          data
        ),
        isDeleted: true,
      };
    }
    return null;
  }, [expense.imputationLeafId, imputationChildren, staleImputationLeaf, data, data.t.lang, data.xpensCategories]);

  const imputationVerifyCategory = useMemo(() => {
    const leafRow = expense.imputationLeafId
      ? imputationChildren.find(
          (r) => imputationDocumentId(r) === String(expense.imputationLeafId)
        )
      : null;
    const level1Row = expense.imputationLevel1Id
      ? imputationRoots.find(
          (r) => imputationDocumentId(r) === String(expense.imputationLevel1Id)
        )
      : null;
    return (
      resolveVerifyCategoryFromImputationRow(leafRow)
      ?? resolveVerifyCategoryFromImputationRow(level1Row)
    );
  }, [expense.imputationLevel1Id, expense.imputationLeafId, imputationRoots, imputationChildren]);

  // onChange patches set imputationVerifyCategory immediately; this effect re-syncs after
  // imputation roots/children load async (e.g. pre-filled review form) for submit validation.
  useEffect(() => {
    const prev = expenseRef.current.imputationVerifyCategory;
    const next = imputationVerifyCategory ?? null;
    if (prev === next) {
      return;
    }
    // Avoid a spurious parent update on mount when no imputation is selected yet.
    if (next == null && (prev == null || prev === undefined) && !expenseRef.current.imputationLevel1Id) {
      return;
    }
    updateInfo({ imputationVerifyCategory: next });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- sync derived verify category for submit validation
  }, [imputationVerifyCategory]);


  useEffect(() => {
    const isActivityRequired = isActivityVisible(expense.matter, isExpenseBillable(expense), isElite);
    const isLocationRequired = Utils.hasExpenseField(data, 'field_location', false)
      && isLocationVisible(expense.costType, expense.category, isElite, imputationVerifyCategory);
    const hasRequiredInfoMissing =
      (Utils.isExpenseFieldRequired(data, 'field_supplier_event') && isBlank(expense.info?.supplier_event)) ||
      (Utils.isExpenseFieldRequired(data, 'field_narrative') && isBlank(expense.info?.narrative)) ||
      (Utils.isExpenseFieldRequired(data, 'field_tips') && isBlank(expense.info?.tip)) ||
      (Utils.isExpenseFieldRequired(data, 'field_tax_code') && isBlank(expense.info?.taxCode));
    const newErrors = {
      matter: Utils.isExpenseFieldRequired(data, 'field_matter') && !expense.matter && expense.expenseType !== 'internal',
      date: Utils.isExpenseFieldRequired(data, 'field_expense_date') && !expense.date,
      costType: Utils.isExpenseFieldRequired(data, 'field_cost_type') && !expense.costType,
      info: !expense.info || !expense.info.amount || hasRequiredInfoMissing,
      activity: isActivityRequired && Utils.isExpenseFieldRequired(data, 'field_activity') && !expense.activity,
      location: isLocationRequired && !expense.info?.location,
      imputationLevel1:
        Utils.isExpenseFieldRequired(data, 'field_imputation_level1') &&
        (!expense.imputationLevel1Id || String(expense.imputationLevel1Id).trim() === ''),
      imputationLeaf:
        Boolean(expense.imputationLevel1Id) &&
        !imputationChildrenLoading &&
        imputationChildren.length > 0 &&
        (!expense.imputationLeafId || String(expense.imputationLeafId).trim() === ''),
    };

    setErrors(newErrors);

    if (onValidationChange) {
      const imputationChildrenPending =
        Utils.hasExpenseField(data, 'field_imputation_level1', false) &&
        Boolean(expense.imputationLevel1Id) &&
        imputationChildrenLoading;
      const isFormValid =
        !Object.values(newErrors).some((error) => error) && !imputationChildrenPending;
      onValidationChange(expenseIndex, isFormValid);
    }
  }, [
    expense.matter,
    expense.expenseType,
    expense.date,
    expense.costType,
    expense.isBillable,
    expense.info,
    expense.info?.amount,
    expense.info?.supplier_event,
    expense.info?.narrative,
    expense.info?.tip,
    expense.info?.taxCode,
    expense.info?.location,
    expense.activity,
    expense.imputationLevel1Id,
    expense.imputationLeafId,
    imputationVerifyCategory,
    imputationChildren.length,
    imputationChildrenLoading,
    expenseIndex,
    onValidationChange,
    data,
  ]);

  const { matter, date: expenseDate, activity, info: expenseInfo, costType, category: aiCategory } = expense;
  const expenseIsBillable = isExpenseBillable(expense);
  const billableOptions = useMemo(
    () => [
      { value: true, label: data.t.read('review_expense.billable.billable') },
      { value: false, label: data.t.read('review_expense.billable.non_billable') },
    ],
    [data.t]
  );
  const billableValue = billableOptions.find((option) => option.value === expenseIsBillable);

  const imputationRootOptions = useMemo(() => {
    const options = sortImputationRowsByDisplayLabel(imputationRoots, data.t.lang, data).map((r) => ({
      value: r,
      label: imputationDisplayLabel(r, data),
    }));
    const l1 = expense.imputationLevel1Id;
    if (
      staleImputationRoot &&
      l1 &&
      imputationDocumentId(staleImputationRoot) === String(l1)
    ) {
      options.unshift({
        value: staleImputationRoot,
        label: deletedImputationOptionLabel(
          staleImputationRoot,
          data.t.read('review_expense.imputation_deleted'),
          data
        ),
        isDeleted: true,
      });
    }
    return options;
  }, [imputationRoots, data, data.t.lang, data.xpensCategories, staleImputationRoot, expense.imputationLevel1Id]);

  const imputationLeafOptions = useMemo(() => {
    const options = sortImputationRowsByDisplayLabel(imputationChildren, data.t.lang, data).map((r) => ({
      value: r,
      label: imputationDisplayLabel(r, data),
    }));
    const leafId = expense.imputationLeafId;
    if (
      staleImputationLeaf &&
      leafId &&
      imputationDocumentId(staleImputationLeaf) === String(leafId)
    ) {
      options.unshift({
        value: staleImputationLeaf,
        label: deletedImputationOptionLabel(
          staleImputationLeaf,
          data.t.read('review_expense.imputation_deleted'),
          data
        ),
        isDeleted: true,
      });
    }
    return options;
  }, [imputationChildren, data, data.t.lang, data.xpensCategories, staleImputationLeaf, expense.imputationLeafId]);

  const formatDeletedImputationLabel = (option) =>
    formatImputationSelectOptionLabel(
      option,
      data.t.read('review_expense.imputation_deleted')
    );

  const validateInput = useCallback((value, name) => {
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: !value,
    }));

    setTouched((prevTouched) => ({
      ...prevTouched,
      [name]: true,
    }));
  }, []);

  const handleInputChange = useCallback(
    (name, value) => {
      validateInput(value, name);
      updateInfo({
        [name]: value,
      });
    },
    [updateInfo, validateInput]
  );

  const handleSplitsChange = useCallback(
    (splits) => {
      updateInfo({ matterSplits: splits });
    },
    [updateInfo]
  );

  const handleInfoUpdate = useCallback(
    (name, value) => {
      updateInfo({
        info: {
          [name]: value,
        },
      });
    },
    [updateInfo]
  );

  const handleBlur = useCallback((fieldName) => {
    setTouched((prev) => ({ ...prev, [fieldName]: true }));
  }, []);

  const handleValidationChange = useCallback((field, isValid) => {
    setErrors((prev) => ({
      ...prev,
      [field]: !isValid,
    }));
    setTouched((prev) => ({
      ...prev,
      [field]: true,
    }));
  }, []);

  const getAdditionalComponent = useCallback(() => {
    const additionalComponents = [];

    if (allowMileage && isMileageVisible(costType, aiCategory)) {
      additionalComponents.push(
        <MileageInput
          key='distance'
          distance={expenseInfo.distance}
          onUpdate={(val) => handleInfoUpdate('distance', val)}
        />
      );
    }

    if (Utils.hasExpenseField(data, 'field_location', false) && isLocationVisible(costType, aiCategory, isElite, imputationVerifyCategory)) {
      additionalComponents.push(
        <LocationInput
          key='location'
          location={expenseInfo.location}
          onUpdate={(updatedLocation) => handleInfoUpdate('location', updatedLocation)}
          onValidationChange={handleValidationChange}
        />
      );
    }

    if (shouldShowNumberOfGuests(data, costType, aiCategory, imputationVerifyCategory)) {
      additionalComponents.push(
        <GuestCountInput
          key='numberOfGuests'
          numberOfGuests={expenseInfo.numberOfGuests}
          required={isNumberOfGuestsRequired(data, costType, aiCategory, imputationVerifyCategory)}
          onUpdate={(updatedGuestCount) =>
            handleInfoUpdate('numberOfGuests', updatedGuestCount)
          }
          onValidationChange={handleValidationChange}
        />
      );
    }

    return additionalComponents.length > 0 ? additionalComponents : null;
  }, [expenseInfo, costType, aiCategory, imputationVerifyCategory, data, isElite, handleInfoUpdate, handleValidationChange, allowMileage]);

  // XPEN-3652: the main fields are drawn in the admin's saved order (Custom mode
  // only, see Utils.getExpenseFieldOrder). Each block keeps its own visibility
  // condition, so the order changes where a field is drawn, never whether.
  const fieldBlocks = {
    field_expense_date: (
      <>
        {Utils.hasExpenseField(data, 'field_expense_date') && <FormField
          label={getExpenseFormLabel(data, 'field_expense_date')}
          required={Utils.isExpenseFieldRequired(data, 'field_expense_date')}
          error={errors.date}
          touched={touched.date}
          errorMessage={data.t.read('form_errors.required_field')}
        >
          <input
            className={`form-control ${errors.date && touched.date ? 'is-invalid' : ''}`}
            required={Utils.isExpenseFieldRequired(data, 'field_expense_date')}
            type='date'
            name='date'
            data-testid='date-input'
            value={expenseDate}
            onChange={(e) => {
              handleInputChange('date', e.target.value);
              onExpenseDateChange?.(e.target.value);
            }}
            onBlur={() => handleBlur('date')}
          />
        </FormField>}
      </>
    ),
    field_expense_type: (
      <>
        {Utils.hasExpenseField(data, 'field_expense_type', false) && (
          <FormField
            className='expense-type-field'
            label={getExpenseFormLabel(data, 'field_expense_type')}
            required={Utils.isExpenseFieldRequired(data, 'field_expense_type')}
          >
            <Select
              name='expenseType'
              value={
                expense.expenseType
                  ? {
                      value: expense.expenseType,
                      label: data.t.read(`review_expense.expense_type.${expense.expenseType}`) || expense.expenseType,
                    }
                  : null
              }
              options={[
                { value: 'client', label: data.t.read('review_expense.expense_type.client') },
                { value: 'internal', label: data.t.read('review_expense.expense_type.internal') },
              ]}
              placeholder={data.t.read('review_expense.expense_type.placeholder')}
              onChange={(choice) => {
                const value = choice?.value ?? '';
                const patch = { expenseType: value };
                if (value === 'client') {
                  patch.isBillable = true;
                  Object.assign(patch, billableSetByType);
                } else if (value === 'internal') {
                  patch.isBillable = false;
                } else if (!billableIsShown) {
                  patch.isBillableSetByType = false;
                }
                updateInfo(patch);
              }}
              classNamePrefix='select'
              isClearable
              menuPortalTarget={menuPortalTarget}
              menuPosition='fixed'
            />
          </FormField>
        )}
      </>
    ),
    field_billable: (
      <>
        {Utils.hasExpenseField(data, 'field_billable', false) && expense.expenseType !== 'internal' && (
          <FormField
            label={getExpenseFormLabel(data, 'field_billable')}
            required={Utils.isExpenseFieldRequired(data, 'field_billable')}
          >
            <div data-testid='expense-billable-row'>
              <Select
                name='isBillable'
                value={billableValue}
                options={billableOptions}
                placeholder={data.t.read('review_expense.billable.placeholder')}
                onChange={(choice) => updateInfo({ isBillable: choice?.value ?? true })}
                classNamePrefix='select'
                menuPortalTarget={menuPortalTarget}
                menuPosition='fixed'
              />
            </div>
          </FormField>
        )}
      </>
    ),
    field_matter: (
      <>
        {Utils.hasExpenseField(data, 'field_matter') && expense.expenseType !== 'internal' && (
          <FormField
            label={getExpenseFormLabel(data, 'field_matter')}
            required={Utils.isExpenseFieldRequired(data, 'field_matter')}
            error={errors.matter}
            touched={touched.matter}
            errorMessage={data.t.read('form_errors.required_field')}
          >
            {allowMatterSplit &&
            Utils.hasExpenseField(data, 'field_matter_split', false) &&
            (expense.expenseType === 'client' || !Utils.hasExpenseField(data, 'field_expense_type', false)) &&
            expense.isBillable !== false ? (
              <MatterSplitInput
                data={data}
                matterSplits={expense.matterSplits ?? null}
                singleMatter={matter}
                loadMatters={loadMatters}
                menuPortalTarget={menuPortalTarget}
                onSingleMatterChange={(choice) => handleInputChange('matter', choice ? choice : '')}
                onSplitsChange={handleSplitsChange}
                optionsCacheKey={matterOptionsCacheKey}
                matterError={errors.matter ? data.t.read('form_errors.required_field') : null}
                matterTouched={touched.matter}
                onMatterBlur={() => handleBlur('matter')}
              />
            ) : (
              <AsyncSelect
                key={matterOptionsCacheKey}
                name='matter'
                value={matter}
                defaultOptions
                loadOptions={loadMatters}
                onChange={(choice) => handleInputChange('matter', choice ? choice : '')}
                onBlur={() => handleBlur('matter')}
                data-testid='matter-autocomplete'
                placeholder={data.t.read('review_expense.matter.placeholder')}
                noOptionsMessage={() => data.t.read('no_results_found')}
                cacheOptions={matterOptionsCacheKey}
                isClearable
                styles={getSelectStyles(errors.matter, touched.matter)}
                classNamePrefix='select'
                menuPortalTarget={menuPortalTarget}
                menuPosition='fixed'
              />
            )}
          </FormField>
        )}
      </>
    ),
    field_imputation_level1: (
      <>
        {Utils.hasExpenseField(data, 'field_imputation_level1', false) && (
          <FormField
            label={getExpenseFormLabel(data, 'field_imputation_level1')}
            required={Utils.isExpenseFieldRequired(data, 'field_imputation_level1')}
            error={errors.imputationLevel1}
            touched={touched.imputationLevel1}
            errorMessage={data.t.read('form_errors.required_field')}
          >
            <Select
              name='imputationLevel1'
              inputId='imputation-level1-select'
              value={rootSelectValue}
              options={imputationRootOptions}
              placeholder={data.t.read('review_expense.imputation_level1.placeholder')}
              formatOptionLabel={formatDeletedImputationLabel}
              onChange={(choice) => {
                const row = choice?.value ?? null;
                if (isSoftDeletedImputation(row)) {
                  return;
                }
                setStaleImputationRoot(null);
                setStaleImputationLeaf(null);
                const patch = {
                  imputationLevel1Id: row ? imputationDocumentId(row) : null,
                  imputationLeafId: null,
                  imputationVerifyCategory: row ? resolveVerifyCategoryFromImputationRow(row) : null,
                };
                if (!expenseTypeIsShown) {
                  // XPEN-3653: the category decides the type; clearing it goes
                  // back to the chosen type, or none. Entering internal makes it
                  // non-billable and remembers the Billable it replaced. Any Client
                  // pick, Client to Client included, and clearing back to a chosen
                  // Client type (add-expense window) make it billable (XPEN-3653-006). Clearing from internal to no type restores the
                  // remembered Billable (a form that opened already internal
                  // remembered nothing, so it keeps the Billable it has).
                  const previousType = expenseRef.current.expenseType || '';
                  const nextType = row ? hiddenExpenseTypeFor(row, null) : (chosenExpenseType ?? '');
                  patch.expenseType = nextType;
                  if (nextType === 'internal' && previousType !== 'internal') {
                    patch.isBillableBeforeInternal = expenseRef.current.isBillable;
                    patch.isBillable = false;
                  } else if (nextType === 'client') {
                    patch.isBillable = true;
                    Object.assign(patch, billableSetByType);
                  } else if (nextType !== 'internal' && previousType === 'internal') {
                    patch.isBillable = expenseRef.current.isBillableBeforeInternal ?? expenseRef.current.isBillable ?? true;
                  }
                  if (!nextType && !billableIsShown) {
                    patch.isBillableSetByType = false;
                  }
                } else if (row?.default_expense_type) {
                  patch.expenseType = row.default_expense_type;
                  patch.isBillable = (row.default_expense_type === 'client');
                  if (row.default_expense_type === 'client') {
                    Object.assign(patch, billableSetByType);
                  }
                }
                Object.assign(patch, buildImputationTaxCodeInfoPatch(data, row, null));
                Object.assign(patch, buildImputationCodingPatch(row, null));
                // XPEN-3759: picking a category clears the subcategory, so its own rule
                // applies (a category with subcategories keeps one, XPEN-3781).
                clearMattersNotAllowed(appliedMatterRule(row), patch);
                onImputationRowsChange?.({ level1Row: row, leafRow: null });
                updateInfo(patch);
                setTouched((t) => ({ ...t, imputationLevel1: true }));
              }}
              onBlur={() => handleBlur('imputationLevel1')}
              data-testid='imputation-level1-select'
              styles={getSelectStyles(errors.imputationLevel1, touched.imputationLevel1)}
              classNamePrefix='select'
              isClearable
              menuPortalTarget={menuPortalTarget}
              menuPosition='fixed'
            />
          </FormField>
        )}
      </>
    ),
    field_imputation_leaf: (
      <>
        {Utils.hasExpenseField(data, 'field_imputation_level1', false) &&
          expense.imputationLevel1Id &&
          (imputationChildrenLoading || imputationChildren.length > 0 || Boolean(staleImputationLeaf)) && (
          <FormField
            label={getExpenseFormLabel(data, 'field_imputation_leaf')}
            required={imputationChildren.length > 0}
            error={errors.imputationLeaf}
            touched={touched.imputationLeaf}
            errorMessage={data.t.read('form_errors.required_field')}
          >
            <Select
              name='imputationLeaf'
              inputId='imputation-leaf-select'
              value={leafSelectValue}
              options={imputationLeafOptions}
              placeholder={data.t.read('review_expense.imputation_subcategory.placeholder')}
              formatOptionLabel={formatDeletedImputationLabel}
              onChange={(choice) => {
                const row = choice?.value ?? null;
                if (isSoftDeletedImputation(row)) {
                  return;
                }
                setStaleImputationLeaf(null);
                const parentRow = imputationRoots.find(
                  (r) => imputationDocumentId(r) === String(expenseRef.current.imputationLevel1Id)
                );
                const patch = {
                  imputationLeafId: row ? imputationDocumentId(row) : null,
                  imputationVerifyCategory: row
                    ? (resolveVerifyCategoryFromImputationRow(row)
                      ?? resolveVerifyCategoryFromImputationRow(parentRow))
                    : resolveVerifyCategoryFromImputationRow(parentRow),
                };
                Object.assign(patch, buildImputationTaxCodeInfoPatch(data, parentRow, row));
                Object.assign(patch, buildImputationCodingPatch(parentRow, row));
                // XPEN-3759: the rule the server applies to the id sent next. A subcategory
                // without its own rule follows the category's (XPEN-3781); clearing the
                // subcategory falls back to the category's own.
                clearMattersNotAllowed(
                  row ? appliedMatterRule(row, { parentRow }) : appliedMatterRule(parentRow),
                  patch
                );
                onImputationRowsChange?.({ level1Row: parentRow ?? null, leafRow: row });
                updateInfo(patch);
                setTouched((t) => ({ ...t, imputationLeaf: true }));
              }}
              onBlur={() => handleBlur('imputationLeaf')}
              data-testid='imputation-leaf-select'
              styles={getSelectStyles(errors.imputationLeaf, touched.imputationLeaf)}
              classNamePrefix='select'
              isClearable
              menuPortalTarget={menuPortalTarget}
              menuPosition='fixed'
            />
          </FormField>
        )}
      </>
    ),
    field_cost_type: (
      <>
        {Utils.hasExpenseField(data, 'field_cost_type') && <FormField
          label={getExpenseFormLabel(data, 'field_cost_type')}
          required={Utils.isExpenseFieldRequired(data, 'field_cost_type')}
          error={errors.costType}
          touched={touched.costType}
          errorMessage={data.t.read('form_errors.required_field')}
        >
          <AsyncSelect
            name='costType'
            value={costType}
            loadOptions={loadCostTypes}
            defaultOptions
            cacheOptions
            isClearable
            placeholder={data.t.read('review_expense.expense_types.placeholder')}
            onChange={(choice) => {
              const nextCostType = choice ?? '';
              const code = typeof nextCostType === 'string'
                ? nextCostType
                : (nextCostType?.value?.cost_type ?? '');
              const patch = { costType: nextCostType };
              const prevCoding = expenseRef.current.imputationCoding;
              if (prevCoding && typeof prevCoding === 'object') {
                // Keep GL/office from subcategory patch; sync cost_type so save honors manual pick.
                patch.imputationCoding = { ...prevCoding, cost_type: code || '' };
              }
              validateInput(nextCostType, 'costType');
              updateInfo(patch);
            }}
            onBlur={() => handleBlur('costType')}
            styles={getSelectStyles(errors.costType, touched.costType)}
            classNamePrefix='select'
            menuPortalTarget={menuPortalTarget}
            menuPosition='fixed'
          />
        </FormField>}
      </>
    ),
    field_activity: (
      <>
        {Utils.hasExpenseField(data, 'field_activity') && isActivityVisible(matter, expenseIsBillable, isElite) && <FormField
          label={getExpenseFormLabel(data, 'field_activity')}
          required={Utils.isExpenseFieldRequired(data, 'field_activity')}
          error={errors.activity}
          touched={touched.activity}
          errorMessage={data.t.read('form_errors.required_field')}
        >
          <AsyncSelect
            styles={getSelectStyles(errors.activity, touched.activity)}
            menuPortalTarget={menuPortalTarget}
            required
            menuPosition='fixed'
            value={activity}
            onChange={(choice) => {
              handleInputChange('activity', choice ?? '');
            }}
            onBlur={() => handleBlur('activity')}
            classNamePrefix='select'
            name='activity-select'
            placeholder={data.t.read('invoice.activity')}
            isClearable
            defaultOptions
            cacheOptions
            loadOptions={loadActivities}
          />
        </FormField>}
      </>
    ),
  };

  return (
    <div className={showHeading ? 'border-bottom pb-5 my-4 expense-container' : 'expense-container'}>
      {deletable && <FontAwesomeIcon icon={faTrash} className='remove-expense' onClick={() => removeExpense()} aria-label={`Remove expense ${expenseIndex + 1}`} />}

      {showHeading && <h2>{`${data.t.read('review_expense.expense')} ${expenseIndex + 1}`}</h2>}

      {Utils.getExpenseFieldOrder(data).map((slot) => (
        cardLayout ? (
          <div key={slot} className={`expense-review-slot ${slot}`}>{fieldBlocks[slot]}</div>
        ) : <Fragment key={slot}>{fieldBlocks[slot]}</Fragment>
      ))}

      {hasSegmentRules(data, GL_FACILITY) && (facilityPreview ? (
        <ReviewFacilityField preview={facilityPreview} data={data} onChange={(line) => updateInfo({ facilityLine: line, facilityLineTouched: true })} />
      ) : !expenseIsBillable && (
        <FormField label={data.t.read('gl_segment.facility')}>
          <SegmentRuleField
            item={expense.facilityLine || {}}
            segment={GL_FACILITY}
            data={data}
            label={data.t.read('gl_segment.facility')}
            onChange={(line) => updateInfo({ facilityLine: line, facilityLineTouched: true })}
          />
        </FormField>
      ))}

      <ExpenseInfo
        cardLayout={cardLayout}
        menuPortalTarget={menuPortalTarget}
        hideNarrative={hideNarrative}
        compactOptionalFields={compactOptionalFields}
        expenseInfo={expenseInfo}
        setExpenseInfo={(value) => handleInputChange('info', value)}
        additionalComponent={getAdditionalComponent()}
        onValidationChange={handleValidationChange}
        locationError={errors.location}
        locationTouched={touched.location}
        onLocationBlur={() => handleBlur('location')}
      />
    </div>
  );
};

export default inject('data')(ExpenseComponent);
